export const AMAP_SECURITY_JS_CODE = 'b86adf78765ef8a51e61d55b24bcc9b2';
export const AMAP_API_KEY = 'bc8a8498437885db47c7dc2963151e7e';
export const AMAP_VERSION = '2.0';
export const AMAP_PLUGINS = 'AMap.Geocoder,AMap.AutoComplete,AMap.Geolocation';

let amapLoading = null;

export const loadAMap = () => {
	if (window.AMap) {
		return Promise.resolve(window.AMap);
	}
	if (amapLoading) {
		return amapLoading;
	}

	window._AMapSecurityConfig = {
		securityJsCode: AMAP_SECURITY_JS_CODE
	};

	amapLoading = new Promise((resolve, reject) => {
		const existed = document.querySelector('script[data-amap]');
		if (existed) {
			existed.addEventListener('load', () => resolve(window.AMap));
			existed.addEventListener('error', () => {
				amapLoading = null;
				reject(new Error('高德地图加载失败'));
			});
			return;
		}
		const script = document.createElement('script');
		script.src =
			`https://webapi.amap.com/maps?v=${AMAP_VERSION}&key=${AMAP_API_KEY}&plugin=${AMAP_PLUGINS}`;
		script.async = true;
		script.defer = true;
		script.dataset.amap = 'true';
		script.onload = () => resolve(window.AMap);
		script.onerror = () => {
			amapLoading = null;
			reject(new Error('高德地图加载失败'));
		};
		document.head.appendChild(script);
	});

	return amapLoading;
};

const ensureAMapPlugins = (AMap, plugins) => new Promise((resolve) => {
	if (!plugins || !plugins.length) {
		resolve();
		return;
	}
	AMap.plugin(plugins, () => resolve());
});

const getCurrentPosition = (positionOptions) => new Promise((resolve, reject) => {
	window.navigator.geolocation.getCurrentPosition(resolve, reject, positionOptions);
});

const getAMapCurrentPosition = (AMap, geolocationOptions = {}) => new Promise((resolve, reject) => {
	AMap.plugin('AMap.Geolocation', () => {
		const geolocation = new AMap.Geolocation({
			enableHighAccuracy: true,
			timeout: 20000,
			maximumAge: 0,
			convert: true,
			showButton: false,
			showMarker: false,
			showCircle: false,
			zoomToAccuracy: false,
			panToLocation: false,
			...geolocationOptions
		});
		geolocation.getCurrentPosition((status, result) => {
			if (status === 'complete' && result && result.position) {
				resolve(result);
				return;
			}
			reject(result || new Error('高德定位失败'));
		});
	});
});

const normalizeCodeString = (value) => {
	if (value === undefined || value === null) return '';
	const normalized = `${value}`.trim();
	if (!normalized || normalized === 'null' || normalized === 'undefined') return '';
	return normalized;
};

const buildProvinceCodeFromAdcode = (adcode) => {
	const normalized = normalizeCodeString(adcode);
	if (normalized.length < 3) return null;
	return `${normalized.substring(0, 3)}000000000`;
};

const buildDistrictCodeFromAdcode = (adcode) => {
	const normalized = normalizeCodeString(adcode);
	if (!normalized) return null;
	return `${normalized}000000`;
};

export const amapPicker = {
	openMapPicker(vm) {
		vm.mapDialogVisible = true;
	},
	async onMapDialogOpen(vm) {
		try {
			vm.mapSearchKeyword = '';
			vm.mapLocating = false;
			amapPicker.seedMapSelectionFromStore(vm);
			await amapPicker.initMap(vm);
		} catch (error) {
			console.error('地图加载失败:', error);
			vm.$message.error('地图加载失败，请稍后重试');
		}
	},
	onMapDialogClose(vm) {
		if (vm.map && vm.map.destroy) {
			vm.map.destroy();
		}
		vm.map = null;
		vm.mapMarker = null;
		vm.geocoder = null;
		vm.mapAutoComplete = null;
		vm.mapSearchKeyword = '';
		vm.mapLocating = false;
	},
	handleMapCancel(vm) {
		vm.mapDialogVisible = false;
	},
	handleMapConfirm(vm) {
		if (!vm.mapSelection) {
			vm.mapDialogVisible = false;
			return;
		}
		let addressPath = vm.mapSelection.addressPath || [];
		if (!addressPath.length) {
			const labels = [
				vm.mapSelection.provinceName,
				vm.mapSelection.cityName,
				vm.mapSelection.districtName,
				vm.mapSelection.streetName
			].filter(Boolean);
			addressPath = vm.getValuesByLabels(labels, vm.addressOptions);
		}

		vm.store.provinceName = vm.mapSelection.provinceName || '';
		vm.store.cityName = vm.mapSelection.cityName || '';
		vm.store.districtName = vm.mapSelection.districtName || '';
		vm.store.streetName = vm.mapSelection.streetName || '';

		vm.store.provinceCode = (vm.mapSelection.provinceCode ?? addressPath[0]) ?? null;
		vm.store.cityCode = (vm.mapSelection.cityCode ?? addressPath[1]) ?? null;
		vm.store.districtCode = (vm.mapSelection.districtCode ?? addressPath[2]) ?? null;
		vm.store.streetCode = (vm.mapSelection.streetCode ?? addressPath[3]) ?? null;

		vm.store.address = vm.mapSelection.address || '';
		vm.store.lng = vm.mapSelection.lng ?? null;
		vm.store.lat = vm.mapSelection.lat ?? null;

		if (addressPath && addressPath.length) {
			vm.address = addressPath;
		}

		vm.mapDialogVisible = false;
	},
	async initMap(vm) {
		const AMap = await loadAMap();
		await ensureAMapPlugins(AMap, ['AMap.Geocoder', 'AMap.AutoComplete', 'AMap.Geolocation']);
		await vm.$nextTick();

		if (!vm.$refs.amapContainer) return;

		const defaultCenter = [116.397428, 39.90923];
		let center = defaultCenter;
		const lng = Number(vm.store.lng);
		const lat = Number(vm.store.lat);
		const hasLngLat = Number.isFinite(lng) && Number.isFinite(lat);
		if (hasLngLat) {
			center = [lng, lat];
		}

		if (vm.map && vm.map.destroy) {
			vm.map.destroy();
		}
		vm.map = new AMap.Map(vm.$refs.amapContainer, {
			center,
			zoom: 16,
			resizeEnable: true
		});

		vm.geocoder = new AMap.Geocoder();
		vm.mapAutoComplete = new AMap.AutoComplete({
			city: '全国',
			citylimit: false
		});

		vm.map.on('click', (event) => {
			const {
				lng,
				lat
			} = event.lnglat;
			amapPicker.setMapPoint(vm, lng, lat);
			amapPicker.reverseGeocode(vm, lng, lat);
		});

		if (hasLngLat) {
			amapPicker.setMapPoint(vm, lng, lat, false);
			return;
		}

		const fullAddress = amapPicker.buildFullAddress(vm);
		if (fullAddress) {
			vm.geocoder.getLocation(fullAddress, (status, result) => {
				if (status === 'complete' && result.geocodes && result.geocodes.length > 0) {
					const location = result.geocodes[0].location;
					amapPicker.setMapPoint(vm, location.lng, location.lat, false);
				}
			});
			return;
		}

		await amapPicker.locateToCurrentPosition(vm);
	},
	setMapPoint(vm, lng, lat, center = true) {
		if (!vm.map) return;
		const position = [lng, lat];
		if (!vm.mapMarker) {
			vm.mapMarker = new window.AMap.Marker({
				position
			});
			vm.map.add(vm.mapMarker);
		} else {
			vm.mapMarker.setPosition(position);
		}
		if (center) {
			vm.map.setCenter(position);
		}
	},
	extractLngLat(position) {
		if (!position) return null;
		const lngValue = position.lng !== undefined ? position.lng :
			(typeof position.getLng === 'function' ? position.getLng() : undefined);
		const latValue = position.lat !== undefined ? position.lat :
			(typeof position.getLat === 'function' ? position.getLat() : undefined);
		const lng = Number(lngValue);
		const lat = Number(latValue);
		if (!Number.isFinite(lng) || !Number.isFinite(lat)) {
			return null;
		}
		return {
			lng,
			lat
		};
	},
	getLocateErrorMessage(error) {
		switch (error && error.code) {
			case 1:
				return '定位权限被拒绝，请在浏览器中允许定位权限';
			case 2:
				return '当前位置不可用，请稍后再试';
			case 3:
				return '定位超时，请稍后重试';
			default:
				return '定位失败，请稍后重试';
		}
	},
	getAmapLocateErrorMessage(error) {
		if (!error) return '';
		const raw = `${error.info || error.message || error.msg || ''}`.toLowerCase();
		if (raw.includes('permission') || raw.includes('denied')) {
			return '定位权限被拒绝，请在浏览器中允许定位权限';
		}
		if (raw.includes('timeout')) {
			return '高德定位超时，请检查网络后重试';
		}
		if (raw.includes('position_unavailable') || raw.includes('unavailable')) {
			return '当前位置不可用，请稍后再试';
		}
		return '';
	},
	async locateToCurrentPosition(vm, options = {}) {
		const {
			showLoading = false
		} = options;
		if (!vm.map) return;

		if (showLoading) {
			vm.mapLocating = true;
		}
		try {
			let amapError = null;
			try {
				const AMap = window.AMap || await loadAMap();
				const amapResult = await getAMapCurrentPosition(AMap);
				const lnglat = amapPicker.extractLngLat(amapResult.position);
				if (lnglat) {
					amapPicker.setMapPoint(vm, lnglat.lng, lnglat.lat);
					amapPicker.reverseGeocode(vm, lnglat.lng, lnglat.lat);
					return;
				}
				amapError = new Error('高德定位未返回有效坐标');
			} catch (error) {
				amapError = error;
			}

			let browserError = null;
			if (window.navigator && window.navigator.geolocation) {
				const locateOptionsList = [{
						enableHighAccuracy: false,
						timeout: 10000,
						maximumAge: 300000
					},
					{
						enableHighAccuracy: true,
						timeout: 25000,
						maximumAge: 0
					}
				];

				let position = null;
				for (let i = 0; i < locateOptionsList.length; i += 1) {
					try {
						position = await getCurrentPosition(locateOptionsList[i]);
						break;
					} catch (error) {
						browserError = error;
						const isPermissionDenied = error && error.code === 1;
						const isLastTry = i === locateOptionsList.length - 1;
						if (isPermissionDenied || isLastTry) {
							break;
						}
					}
				}

				if (position && position.coords) {
					const lng = Number(position.coords.longitude);
					const lat = Number(position.coords.latitude);
					if (!Number.isFinite(lng) || !Number.isFinite(lat)) {
						throw new Error('无法获取当前位置坐标');
					}
					amapPicker.setMapPoint(vm, lng, lat);
					amapPicker.reverseGeocode(vm, lng, lat);
					return;
				}
			}

			if (browserError && browserError.code === 1) {
				vm.$message.warning(amapPicker.getLocateErrorMessage(browserError));
				return;
			}
			const amapMsg = amapPicker.getAmapLocateErrorMessage(amapError);
			if (amapMsg) {
				vm.$message.warning(amapMsg);
				return;
			}
			if (browserError) {
				vm.$message.warning(amapPicker.getLocateErrorMessage(browserError));
				return;
			}
			vm.$message.warning('定位失败，请检查浏览器定位权限和网络后重试');
		} catch (error) {
			const msg = error instanceof Error && error.message ?
				error.message :
				amapPicker.getLocateErrorMessage(error);
			vm.$message.warning(msg);
		} finally {
			if (showLoading) {
				vm.mapLocating = false;
			}
		}
	},
	fetchMapSearchSuggestions(vm, keyword, cb) {
		const searchKeyword = (keyword || '').trim();
		if (!searchKeyword || !vm.mapAutoComplete) {
			cb([]);
			return;
		}

		vm.mapAutoComplete.search(searchKeyword, (status, result) => {
			if (status !== 'complete' || !result || !Array.isArray(result.tips)) {
				cb([]);
				return;
			}
			const suggestions = result.tips
				.filter(item => item && item.name)
				.map(item => ({
					value: item.name,
					name: item.name,
					district: item.district || '',
					address: item.address || '',
					location: item.location || null
				}));
			cb(suggestions);
		});
	},
	handleMapSearchSelect(vm, item) {
		if (!item) return;
		vm.mapSearchKeyword = item.name || item.value || '';

		const applyLocation = (lng, lat) => {
			amapPicker.setMapPoint(vm, lng, lat);
			amapPicker.reverseGeocode(vm, lng, lat);
		};

		if (item.location && Number.isFinite(Number(item.location.lng)) && Number.isFinite(Number(item.location.lat))) {
			applyLocation(Number(item.location.lng), Number(item.location.lat));
			return;
		}

		if (!vm.geocoder) {
			vm.$message.warning('搜索结果缺少坐标信息，请尝试其他关键词');
			return;
		}

		const searchText = [item.district, item.name, item.address].filter(Boolean).join('');
		if (!searchText) {
			vm.$message.warning('未找到可定位的地址信息，请尝试其他关键词');
			return;
		}
		vm.geocoder.getLocation(searchText, (status, result) => {
			if (status === 'complete' && result.geocodes && result.geocodes.length > 0) {
				const location = result.geocodes[0].location;
				applyLocation(location.lng, location.lat);
				return;
			}
			vm.$message.warning('未找到该地点，请尝试更具体的关键词');
		});
	},
	reverseGeocode(vm, lng, lat) {
		if (!vm.geocoder) return;
		vm.geocoder.getAddress([lng, lat], async (status, result) => {
			if (status !== 'complete' || !result.regeocode) {
				vm.$message.error('获取地址失败，请重试');
				return;
			}
			const regeo = result.regeocode;
			const comp = regeo.addressComponent || {};
			const province = comp.province || '';
			const city = (comp.city && comp.city.length ? comp.city : comp.province) || '';
			const district = comp.district || '';
			const street = comp.township || comp.street || comp.streetName || '';

			let detailAddress = amapPicker.buildDetailAddress(regeo);
			if (!detailAddress && regeo.formattedAddress) {
				detailAddress = regeo.formattedAddress;
			}

			const labels = [province, city, district, street].filter(Boolean);
			const values = vm.getValuesByLabels(labels, vm.addressOptions);
			const adcode = normalizeCodeString(comp.adcode);
			const amapCityCode = normalizeCodeString(comp.citycode);
			const amapTownCode = normalizeCodeString(comp.towncode);
			const amapProvinceCode = buildProvinceCodeFromAdcode(adcode);
			const amapDistrictCode = buildDistrictCodeFromAdcode(adcode);

			vm.mapSelection = {
				provinceName: province,
				provinceCode: amapProvinceCode ?? values[0] ?? null,
				cityName: city,
				cityCode: amapCityCode || values[1] || null,
				districtName: district,
				districtCode: amapDistrictCode ?? values[2] ?? null,
				streetName: street,
				streetCode: amapTownCode || values[3] || null,
				address: detailAddress,
				lng,
				lat,
				addressPath: values
			};
		});
	},
	buildFullAddress(vm) {
		const parts = [
			vm.store.provinceName,
			vm.store.cityName,
			vm.store.districtName,
			vm.store.streetName,
			vm.store.address
		].filter(Boolean);
		return parts.join('');
	},
	buildDetailAddress(regeo) {
		if (!regeo) return '';
		const comp = regeo.addressComponent || {};
		const province = comp.province || '';
		const city = (comp.city && comp.city.length ? comp.city : '') || '';
		const district = comp.district || '';
		const cityPart = city && city !== province ? city : '';
		const region = `${province}${cityPart}${district}`;

		const detailParts = [];
		if (comp.township) detailParts.push(comp.township);
		if (comp.street) detailParts.push(comp.street);
		if (comp.streetNumber) {
			const streetNumber = typeof comp.streetNumber === 'string' ?
				comp.streetNumber :
				(comp.streetNumber.number || '');
			if (streetNumber) detailParts.push(streetNumber);
		}
		if (comp.neighborhood && comp.neighborhood.name) detailParts.push(comp.neighborhood.name);
		if (comp.building && comp.building.name) detailParts.push(comp.building.name);

		const detail = detailParts.filter(Boolean).join('');
		if (detail) return detail;

		let formatted = regeo.formattedAddress || '';
		if (region && formatted.startsWith(region)) {
			formatted = formatted.slice(region.length);
		} else if (province && district && formatted.startsWith(`${province}${district}`)) {
			formatted = formatted.slice(`${province}${district}`.length);
		}
		return formatted || '';
	},
	seedMapSelectionFromStore(vm) {
		if (!vm.store) return;
		vm.mapSelection = {
			provinceName: vm.store.provinceName || '',
			provinceCode: vm.store.provinceCode ?? null,
			cityName: vm.store.cityName || '',
			cityCode: vm.store.cityCode ?? null,
			districtName: vm.store.districtName || '',
			districtCode: vm.store.districtCode ?? null,
			streetName: vm.store.streetName || '',
			streetCode: vm.store.streetCode ?? null,
			address: vm.store.address || '',
			lng: vm.store.lng ?? null,
			lat: vm.store.lat ?? null,
			addressPath: vm.address || []
		};
	}
};
